<template>

  <div
    id="booking"
    class="container-fluid bookatron"
    :class="$deploymentIs('cryptology') ? 'bookatron-cryptology text-black' : 'text-white'">
    <div class="row py-4" id="calHeader">
      <div class="col d-none d-md-block"></div>
      <div class="col-md-6 text-center">
        <h2 v-if="currentRoomId" class="font-weight-bold">Booking for {{ location.rooms.find(room => room.id == currentRoomId).name }}</h2>
        <h2 v-else class="font-weight-bold">Available Games for {{ location.name }}</h2>
      </div>
      <div class="col d-none d-md-block"></div>
    </div>

    <div class="row pb-4 pr-0 vld-parent" :id="$id('calendar')" ref='calendarContainer'>

      <call-for-booking
        ref="callForBooking"
        :location="location"
        :brand="brand"/>

      <make-booking
        ref="makeBooking"
        :initial-cart="initialCart"
        :location="location"
        :saleFrom="saleFrom"
        :locationDiscount="locationDiscount"
        :brand="brand"
        :location-child-pricing="locationChildPricing"
        :customer-request-marketing-permission="customerRequestMarketingPermission"
        :payment-provider="paymentProvider"
        @refetch="refetchRoomSlots"/>

      <div class="col d-none d-md-block"></div>
      <div
        class="mainCal col-md-6 text-center px-0 mr-0"
        :class="$deploymentIs('cryptology') ? 'mainCal-cryptology' : ''"
        :id="$id('mainCal')">
        <h4 :class="$deploymentIs('cryptology') ? 'calHeader-cryptology' : 'calHeader'">Select Date</h4>
        <div class="row" :id="$id('calSubHeader')">
          <div class="col">
            <a
              href="#"
              rel="nofollow"
              class="calPrevNext"
              @click.prevent="previousMonthClicked">
              <i class="material-icons">skip_previous</i>
            </a>
          </div>
          <div class="col-5 calTitle" :id="$id('calMonth')">{{ date.format('MMMM YYYY') }}</div>
          <div class="col">
            <a
              href="#"
              rel="nofollow"
              class="calPrevNext"
              @click.prevent="nextMonthClicked">
              <i class="material-icons">skip_next</i>
            </a>
          </div>
        </div>
        <div class="row" :id="$id('calDays')">
          <div class="col calDays">
            <span>Sun</span>
            <span>Mon</span>
            <span>Tue</span>
            <span>Wed</span>
            <span>Thu</span>
            <span>Fri</span>
            <span>Sat</span>
          </div>
        </div>
        <div class="row" :id="$id('calDates')">
          <div
            class="col"
            :class="$deploymentIs('cryptology') ? 'dates-cryptology' : 'dates'">
            <span v-for="n in date.clone().startOf('month').day()" :key="n">&nbsp;</span>
            <template v-for="dayInMonth in date.daysInMonth()">
              <a
                v-if="isMonthDaySelectable(dayInMonth)"
                href="#"
                rel="nofollow"
                class="text-decoration-none timeSlotCalNotBooked timeSlotCalHalfBooked"
                :class="$deploymentIs('cryptology') ? 'timeSlotCalHalfBooked-cryptology' : ''"
                @click.prevent="selectDayInMonth(dayInMonth)">
               {{ dayInMonth }}
              </a>
              <span
                v-else
                :class="{'timeSlotCalBooked': isMonthDayFullyBooked(dayInMonth), 'timeSlotCalBooked-cryptology': isMonthDayFullyBookedandCryptology(dayInMonth)}">
               {{ dayInMonth }}
              </span>
            </template>
          </div>
        </div>
        <div class="row text-center" :id="$id('calKey')">
          <div class="col mb-2">
            <div class="row my-2">
              <div class="col">
                <span class="calKey" :class="$deploymentIs('cryptology') ? 'calKeyBooked-cryptology' : 'calKeyBooked'"></span> Fully Booked
              </div>
              <div class="col">
                <span class="calKey" :class="$deploymentIs('cryptology') ? 'calKeyAvail-cryptology' : 'calKeyAvail'"></span> Slot(s) Available
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4 text-center pl-0" :class="$deploymentIs('cryptology') ? 'calTimeslots-cryptology' : 'calTimeslots'" :id="$id('timeslotCal')">
        <h4 :class="$deploymentIs('cryptology') ? 'calHeader-cryptology' : 'calHeader'">Select Timeslot</h4>
        <h5 class="my-4">
          Available timeslots for <span id="todayPretty">{{ date.format('dddd D') }}<sup>{{ date.format('Do').slice(-2) }}</sup> {{ date.format('MMMM YYYY') }}</span>
        </h5>
        <div class="row">
          <div class="col timeslots pt-2 ml-2 text-left" id="tmsl">
            <span v-if="roomSlots.length == 0">Please call us for a booking in this month. Our number is <a :href="'tel:' + location.telephone">{{ location.telephone }}</a> or visit our <a class="btn btn-yellow" :href="contactRoute">Contact Page</a></span>
            <span v-else-if="typeof roomSlotsForDate === 'undefined' || roomSlotsForDate.length == 0">There are no bookings available for this day.</span>
            <template v-else-if="currentRoomId">
              <!-- Single room list -->
              <template v-for="roomSlot in roomSlotsForDate">
                <a
                  v-if="slotClickable(roomSlot)"
                  :key="roomSlot.id"
                  href="#"
                  class="pr-4"
                  :class="slotStateClass(roomSlot) + ' ' + ($deploymentIs('cryptology') ?'timeSlotCal timeSlotCal-cryptology' : 'timeSlotCal')"
                  @click.prevent="roomSlotClicked(roomSlot)">
                  {{ roomSlot.start | moment('h:mm a') }} - {{ slotStateStringForDisplay(roomSlot) }}
                </a>
                <span
                  v-else
                  :key="roomSlot.id"
                  class="timeSlotCal timeSlotCalBooked pr-4"
                  :class="$deploymentIs('cryptology') ? 'timeSlotCalBooked-cryptology' : ''">
                 {{ roomSlot.start | moment('h:mm a') }} - {{ slotStateStringForDisplay(roomSlot) }}
                </span>
              </template>
            </template>
            <template v-else>
              <!-- Multiple room list -->
              <template v-for="roomSlot in roomSlotsForDate">
                 <!-- TODO: group by time slot and just show a room button-->
                <a
                  v-if="slotClickable(roomSlot)"
                  :key="roomSlot.id"
                  href="#"
                  class="timeSlotCal col-12 col-lg-6 col-xl-4"
                  :class="slotStateClass(roomSlot) + ' ' + ($deploymentIs('cryptology') ?'timeSlotCal timeSlotCal-cryptology' : 'timeSlotCal')"
                  @click.prevent="roomSlotClicked(roomSlot)">
                  {{ roomSlot.start | moment('h:mm a') }} - {{ roomFor(roomSlot).name }}
                </a>
                <!-- <span v-else
                  :key="roomSlot.id"
                  class="timeSlotCal timeSlotCalBooked">
                 {{ roomSlot.start | moment('h:mm a') }} - {{ roomFor(roomSlot).name }}
                </span> -->
              </template>
            </template>
          </div>
        </div>
        <div v-if="otherRoomsAvailableForDate" class="row">
          <div class="col pt-2" id="otherRooms">
            <div v-if="otherRoomsAvailableForDate.length == 0">
              <h4 class='my-4'>There are no other available Escape Room slots on this day.</h4>
            </div>
            <div v-else>
              <h4>Slots also available on this day in&hellip;</h4>
              <a
                v-for="room in otherRoomsAvailableForDate"
                :key="room.id"
                :href="route('cryptology.location.room', {location: location.slug, room: room.slug, dt: date.format('YYYY-MM-DD')}) + '#book'"
                class="text-white m-3 btn"
                :class="'btn-' + room.nickname">
               {{ room.name }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="col d-none d-md-block"></div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    initialCart: {
      type: Object,
      required: true,
    },
    location: {
      type: Object,
      required: true,
    },
    currentRoomId: {
      type: Number,
      default: null,
    },
    initialDate: {
      type: String,
      required: true,
    },
    saleFrom: {
      type: String,
      default: 'R',
    },
    locationDiscount: {
      type: Object,
    },
    initialRoomSlots: {
      type: [Object, Array],
      // required: true,
    },
    initialRoomsAvailable: {
      type: [Object, Array],
      // required: true,
    },
    brand: {
      type: String,
    },
    locationChildPricing: {
      type: Boolean,
    },
    customerRequestMarketingPermission: {
      type: Boolean,
    },
    paymentProvider: {
      type: String,
      default: 'Testing',
    },
  },

  data() {
    return {
      date: moment(this.initialDate),
      roomSlots: Vue.util.extend({}, JSON.parse(JSON.stringify(this.initialRoomSlots))),
      roomsAvailable: Vue.util.extend({}, JSON.parse(JSON.stringify(this.initialRoomsAvailable))),
    };
  },

  computed: {
    /* ===============
     * timeslotCal computed
     */

    roomSlotsForDate() {
      if (this.currentRoomId && this.roomSlots[this.date.format('YYYY-MM-DD')]) {
        return this.roomSlots[this.date.format('YYYY-MM-DD')]
            .filter(roomSlot => roomSlot.room_id == this.currentRoomId && moment(roomSlot.start).isAfter());
      }

      // return all the roomSlots
      return this.roomSlots[this.date.format('YYYY-MM-DD')];
    },

    otherRoomsAvailableForDate() {
      if (this.currentRoomId && this.roomsAvailable[this.date.format('YYYY-MM-DD')]) {
        let roomIds = this.roomsAvailable[this.date.format('YYYY-MM-DD')]
            .filter(room_id => room_id != this.currentRoomId);

        if (roomIds === null) {
          return null;
        }

        return this.location.rooms
          .filter(room => roomIds.includes(room.id));
      }

      return null;
    },

    todayDateString() {
      return moment().format('YYYY-MM-DD');
    },
    promoDate() {
      if (this.locationDiscount) {
        return moment(this.locationDiscount.date).format('YYYY-MM-DD');
      } else {
        return "N/A";
      }
    },

    /*
     * END timeslotCal methods
     * =============== */

    contactRoute() {
      return this.location.locationContactHref;
    },
  },

  watch: {
    date: {
      handler(newDate, oldDate) {
        this.changeQueryString('dt', newDate.format('YYYY-MM-DD'));

        this.echoDeInit(oldDate);
        this.echoInit(newDate);
      },
      deep: true,
    },
  },

  methods: {
    /* ===============
     * mainCal methods
     */

    isMonthDaySelectable(dayInMonth) {
      let theDate = this.date.clone().date(dayInMonth);

      if (theDate < moment().date()
        || ! ([theDate.format('YYYY-MM-DD')] in this.roomSlots)
        || ([theDate.format('YYYY-MM-DD')] in this.roomSlots && (! ([theDate.format('YYYY-MM-DD')] in this.roomsAvailable)))
      ) {
        return false; // want a span, not a button
      }

      return true; // get a button, not a span
    },

    selectDayInMonth(dayInMonth) {
      this.date = this.date.clone().date(dayInMonth);
    },

    isMonthDayFullyBooked(dayInMonth) {
      // there are bookings, and none are available
      let theDate = this.date.clone().date(dayInMonth);

      if (theDate < moment().date()) {
        return false;
      }

      if (! ([theDate.format('YYYY-MM-DD')] in this.roomSlots)) {
        return false;
      }

      return ! ([theDate.format('YYYY-MM-DD')] in this.roomsAvailable);
    },

    isMonthDayFullyBookedandCryptology(dayInMonth) {
      if (this.$deploymentIs('cryptology')) {
        return this.isMonthDayFullyBooked(dayInMonth);
      }
      return false;
    },

    nextMonthClicked() {
      let newDate = this.date.clone().startOf('month').add(1, 'month');
      this.fetchRoomSlots(newDate);
    },

    previousMonthClicked() {
      let newDate = this.date.clone().startOf('month').subtract(1, 'month');

      if ( newDate.isBefore(moment(), 'day') ) {
        newDate = moment();
      }

      if (this.date.isSame(newDate, 'month')) {
        return;
      }

      this.fetchRoomSlots(newDate);
    },

    refetchRoomSlots() {
        this.fetchRoomSlots(this.date);
    },

    fetchRoomSlots(newDate) {
      let roomSlotsRequest = {
        for_rooms_for_month: 1,
        room_ids: this.location.rooms.map(room => room.id),
        date: newDate.toISOString(true),
      };

      let roomsAvailableRequest = {
        ...roomSlotsRequest,
        availability_only: 1,
      };

      this.loading(true, this.$refs.calendarContainer);

      Promise.all([
        axios.get(this.route('api.room-slots.index'), {params: roomSlotsRequest}),
        axios.get(this.route('api.room-slots.index'), {params: roomsAvailableRequest})
      ])
        .then(results => {
          const roomSlotsResponse = results[0];
          const roomsAvailableResponse = results[1];

          if (roomSlotsResponse.status == 200 && roomsAvailableResponse.status == 200) {
            this.roomSlots = roomSlotsResponse.data;
            this.roomsAvailable = roomsAvailableResponse.data;
            this.date = newDate;
          } else {
            flash('Error fetching data.', 'danger');
            console.log('fetchRoomSlots:roomSlotsResponse', roomSlotsResponse.data);
            console.log('fetchRoomSlots:roomSlotsResponse', roomSlotsResponse.status);
            console.log('fetchRoomSlots:roomSlotsResponse', roomSlotsResponse.statusText);
            console.log('fetchRoomSlots:roomsAvailableResponse', roomsAvailableResponse.data);
            console.log('fetchRoomSlots:roomsAvailableResponse', roomsAvailableResponse.status);
            console.log('fetchRoomSlots:roomsAvailableResponse', roomsAvailableResponse.statusText);
          }

          this.loading(false);
        })
        .catch((error) => {
          flash('Error fetching room slots', 'danger');
          if (error.response) {
            // if HTTP_UNPROCESSABLE_ENTITY some validation error laravel or us
            // else if HTTP_CONFLICT
            // else if HTTP_FORBIDDEN on enough permissions
            if (error.response.status == 401) {
              // else if HTTP_UNAUTHORIZED not authorized
              console.log('fetchRoomSlots:401', error.response.data);
              window.location.reload();
            }
            console.log('fetchRoomSlots: Response error', error.response.data, error.response.status, error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.error('fetchRoomSlots: Request error', error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('fetchRoomSlots: Error', error.message);
          }

          this.loading(false);
        });
    },

    changeQueryString(param, value) {
      let url = new URL(window.location);
      url.searchParams.set(param, value);
      let state = {[param]: value};
      history.pushState(state, '', url);
    },

    /*
     * END mainCal methods
     * =============== */

    /* ===============
     * timeslotCal methods
     */

    slotClickable(roomSlot) {
      switch (roomSlot.state) {
        case 'LOCKED':
        case 'AVAILABLE':
        case 'BOOKED_PENDING':
          return true;
        case 'BOOKED':
        case 'HIDDEN':
        case 'UNSOLD':
        case 'DELETED':
        default:
          return false;
      }
    },

    slotStateClass(roomSlot) {
      switch (roomSlot.state) {
        case 'LOCKED':
        case 'BOOKED_PENDING':
          return 'timeSlotCalLocked';
        case 'BOOKED':
        case 'HIDDEN':
        case 'UNSOLD':
        case 'DELETED':
          return 'timeSlotCalBooked';
        case 'AVAILABLE':
          return 'timeSlotCalNotBookedCal';
        default:
          return '';
      }
    },

    slotStateStringForDisplay(roomSlot) {
      switch (roomSlot.state) {
        case 'HIDDEN':
        case 'UNSOLD':
          return 'Booked'
        default:
          return roomSlot.state_string;
      }
    },

    roomFor(roomSlot) {
      return this.location.rooms.find(room => room.id == roomSlot.room_id);
    },

    roomSlotClicked(roomSlot) {
      // console.log('roomSlotClicked', roomSlot);
      let bookingMinutes = this.location.booking_minutes;
      // console.log('moment less minutes', moment(roomSlot.start).subtract(bookingMinutes, 'minutes').format("dddd, MMMM Do YYYY, h:mm:ss a"));

      if (moment(roomSlot.start).subtract(bookingMinutes, 'minutes') < moment()) {
        // console.log('show callForBooking Modal');
        this.$refs.callForBooking.showModal();
      } else {
        this.$refs.makeBooking.startBooking(roomSlot);
      }
    },

    /*
     * END timeslotCal methods
     * =============== */

    /* ===============
     * Echo methods
     */
    echoInit(date) {
      // console.log('echoInit(date)', date);
      if (! process.env.MIX_PUSHER_APP_KEY) {
        return;
      }

      Echo.channel('booking-calendar.room-slots.' + date.format('YYYY-MM'))
        .listen('BookingCalendar.RoomSlotChanged', this.roomSlotChangedEvent)
        .listen('BookingCalendar.RoomSlotDeleted', this.roomSlotDeletedEvent);
    },

    echoDeInit(date) {
      // console.log('echoDeInit(date)', date);
      if (! process.env.MIX_PUSHER_APP_KEY) {
        return;
      }

      Echo.leave('room-slots.' + date.format('YYYY-MM'));
    },

    roomSlotChangedEvent(event) {
      // console.log('roomSlotChangedEvent(event)', event);
      let roomSlotDate = moment(event.roomSlot.start).format('YYYY-MM-DD');
      if (! this.roomSlots.hasOwnProperty(roomSlotDate)) {
        return;
      }

      let roomSlotIndex = this.roomSlots[roomSlotDate].findIndex(rs => rs.id == event.roomSlot.id);
      if (roomSlotIndex == -1) {
        return;
      }

      this.$set(this.roomSlots[roomSlotDate], roomSlotIndex, event.roomSlot);
    },

    roomSlotDeletedEvent(event) {
      // console.log('roomSlotDeletedEvent(event)', event);
      let roomSlotDate = moment(event.roomSlot.start).format('YYYY-MM-DD');
      if (! this.roomSlots.hasOwnProperty(roomSlotDate)) {
        return;
      }

      let roomSlotIndex = this.roomSlots[roomSlotDate].findIndex(rs => rs.id == event.roomSlot.id);
      if (roomSlotIndex == -1) {
        return;
      }

      this.$delete(this.roomSlots[roomSlotDate], roomSlotIndex);
    },

    /*
     * END Echo methods
     * =============== */
  },

  mounted() {
    this.echoInit(this.date);
  },

  beforeDestroy() {
    this.echoDeInit(this.date);
  },
}
</script>
